import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

/**
 * EAA-1756: thin wrapper over oidc-client-ts's `UserManager` — the frozen
 * auth-client API per design-frontend.md `packages/auth-client`
 * (`createEmgAuth` -> `{ login, handleCallback, getAccessToken, getClaims,
 * logout, onSessionEnded }`). Authorization Code + PKCE (oidc-client-ts
 * defaults a public client to S256), refresh via rotated refresh tokens
 * (`automaticSilentRenew`), and state kept in `sessionStorage` — per-tab,
 * NEVER `localStorage` — so switching sessions in one tab never bleeds into
 * another.
 */

export interface EmgAuthConfig {
    authority: string;
    clientId: string;
    redirectUri: string;
}

export interface EmgAuthClaims {
    sub: string;
    email?: string;
    name?: string;
    sid?: string;
}

export interface EmgAuthClient {
    login(returnTo?: string): Promise<void>;
    handleCallback(): Promise<User>;
    getAccessToken(): Promise<string | null>;
    getClaims(): Promise<EmgAuthClaims | null>;
    logout(): Promise<void>;
    onSessionEnded(cb: () => void): () => void;
}

export function createEmgAuth(config: EmgAuthConfig): EmgAuthClient {
    const origin = window.location.origin;

    const userManager = new UserManager({
        authority: config.authority,
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        post_logout_redirect_uri: `${origin}/`,
        response_type: 'code',
        scope: 'openid profile email',
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });

    async function login(returnTo?: string): Promise<void> {
        await userManager.signinRedirect({
            state: { returnTo: returnTo ?? '/' },
        });
    }

    async function handleCallback(): Promise<User> {
        return userManager.signinRedirectCallback();
    }

    async function getAccessToken(): Promise<string | null> {
        const user = await userManager.getUser();
        if (!user || user.expired) return null;
        return user.access_token;
    }

    async function getClaims(): Promise<EmgAuthClaims | null> {
        const user = await userManager.getUser();
        if (!user || user.expired || !user.profile.sub) return null;
        const { sub, email, name, sid } = user.profile;
        return { sub, email, name, sid };
    }

    async function logout(): Promise<void> {
        await userManager.signoutRedirect();
    }

    function onSessionEnded(cb: () => void): () => void {
        userManager.events.addUserSignedOut(cb);
        userManager.events.addUserSessionChanged(cb);
        userManager.events.addAccessTokenExpired(cb);
        return () => {
            userManager.events.removeUserSignedOut(cb);
            userManager.events.removeUserSessionChanged(cb);
            userManager.events.removeAccessTokenExpired(cb);
        };
    }

    return { login, handleCallback, getAccessToken, getClaims, logout, onSessionEnded };
}
